import type ApolloClient from 'apollo-client';

import EnterpriseGradebookExport from 'bundles/enterprise-admin-program-gradebook/queries/EnterpriseGradebookExportMutation.graphql';
import type {
  EnterpriseGradebookExportMutation,
  EnterpriseGradebookExportMutationVariables,
} from 'bundles/enterprise-admin-program-gradebook/queries/__generated__/EnterpriseGradebookExportMutation';

type ProgramGradebookScopeDefinition = {
  programIds: string[];
};

type CollectionId = {
  programId: string;
  trackId: string;
};

type CollectionGradebookScopeDefinition = {
  collectionId: CollectionId;
};

/**
 * Describes filters shape for the report table
 */
export type GradebookExportOptions = {
  startDate?: string;
  endDate?: string;
  courseId?: string;
  oneCsvPerProgram?: boolean;
  attemptFilter?: 'ALL_ATTEMPTS' | 'ONLY_FIRST_ATTEMPT' | 'ONLY_LATEST_ATTEMPT';
  includeCompletedCoursesOnly?: 'INCLUDE_ALL' | 'INCLUDE_ONLY_COMPLETED_COURSES' | 'INCLUDE_ONLY_INCOMPLETE_COURSES';
  includeDeletedMembers?: boolean;
  isPlagiarismReport?: boolean;
};

// The dateRange should be included only when startDate and endDate exist.
const getDateRangeProp = (startDate?: string, endDate?: string) => {
  return startDate !== undefined && endDate !== undefined
    ? {
        dateRange: {
          startDate,
          endDate,
        },
      }
    : {};
};

const getCommonInputProps = (options: GradebookExportOptions) => {
  const dateRange = getDateRangeProp(options.startDate, options.endDate);

  return {
    courseId: options.courseId,
    includeDeletedMembers: options.includeDeletedMembers,
    oneCsvPerProgram: options.oneCsvPerProgram,
    attemptFilter: options.attemptFilter,
    includeCompletedCoursesOnly: options.includeCompletedCoursesOnly,
    isPlagiarismReport: options.isPlagiarismReport,
    ...dateRange,
  };
};

const getProgramsScopeGraphqlVariables = (
  definition: ProgramGradebookScopeDefinition,
  options: GradebookExportOptions
): EnterpriseGradebookExportMutationVariables => {
  const commonInputProps = getCommonInputProps(options);

  return {
    input: {
      scope: {
        enterpriseProgramsGradebookScope: {
          programIds: definition.programIds,
        },
      },
      ...commonInputProps,
    },
  };
};

const getCollectionScopeGraphqlVariables = (
  definition: CollectionGradebookScopeDefinition,
  options: GradebookExportOptions
): EnterpriseGradebookExportMutationVariables => {
  const commonInputProps = getCommonInputProps(options);

  return {
    input: {
      scope: {
        enterpriseCollectionGradebookScope: {
          collectionId: definition.collectionId,
        },
      },
      ...commonInputProps,
    },
  };
};

const callEnterpriseGradebookExportApi = async (
  apolloClient: ApolloClient<object>,
  variables: EnterpriseGradebookExportMutationVariables
) => {
  const { data, errors } = await apolloClient.mutate<EnterpriseGradebookExportMutation>({
    mutation: EnterpriseGradebookExport,
    variables,
    context: { clientName: 'gatewayGql' },
  });

  const result = data?.EnterpriseGradebooks_EnterpriseItemAttemptsGradebooks;

  // The code calling the export function expects rejected promise in case of failure. Therefore, we need to throw.
  if (result?.__typename === 'EnterpriseGradebooks_ExportJobFailure') {
    const errorMessagesStr = result.errors.map((e) => e.message).join(', ');
    throw new Error(`ExportJobFailure: ${errorMessagesStr}`);
  }

  if (errors && errors.length > 0) {
    const errorMessagesStr = errors.map((e) => e.message).join(', ');
    throw new Error(`GraphQLError: ${errorMessagesStr}`);
  }
};

/**
 * Calls the `EnterpriseGradebooks_EnterpriseItemAttemptsGradebooks` Graphql mutation and triggers the C4C enterprise
 * gradebook export for specific program.
 *
 * The call should trigger the ent-gradebooks-jobs-EnterpriseProgramScopeGradebookExportJob Airflow job:
 * https://airflow-niagara2-prod.dkandu.me/tree?dag_id=ent-gradebooks-jobs-EnterpriseProgramScopeGradebookExportJob
 */
export const exportProgramsEdsGradebook = (
  apolloClient: ApolloClient<object>,
  definition: ProgramGradebookScopeDefinition,
  options: GradebookExportOptions = {}
) => {
  const variables = getProgramsScopeGraphqlVariables(definition, options);
  return callEnterpriseGradebookExportApi(apolloClient, variables);
};

// not used yet
/**
 * Calls the `EnterpriseGradebooks_EnterpriseItemAttemptsGradebooks` Graphql mutation and triggers the C4C enterprise
 * gradebook export for specific collection.
 *
 * The call should trigger the ent-gradebooks-jobs-EnterpriseCollectionScopeGradebookExportJob Airflow job:
 * https://airflow-niagara2-prod.dkandu.me/tree?dag_id=ent-gradebooks-jobs-EnterpriseCollectionScopeGradebookExportJob
 */
export const exportCollectionEdsGradebook = (
  apolloClient: ApolloClient<object>,
  definition: CollectionGradebookScopeDefinition,
  options: GradebookExportOptions = {}
) => {
  const variables = getCollectionScopeGraphqlVariables(definition, options);
  return callEnterpriseGradebookExportApi(apolloClient, variables);
};
